import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Upload,
  Button,
  Icon,
  Tooltip,
  message
} from "antd";
import last from "lodash/last";
import { connect } from "react-redux";
import { Storage } from "aws-amplify";
import { ulid } from "ulid";
import { makeBucketName, deletePublicImage } from "utils/bucket";
import { validImage } from 'utils/validators';
import { tosValidator } from "utils/validators";
import { labActions } from "redux/modules/lab";
import { mappingActions } from "redux/modules/mappings";
import { getTirePrice } from 'utils/price';
import Preview from "./preview";
import * as Styled from "./styled";

const FormItem = Form.Item;

class Mapping extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    catalogs: PropTypes.array,
    labData: PropTypes.object,
    listing: PropTypes.object,
    mapping: PropTypes.object,
    product: PropTypes.object,
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    onChange: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      saving: false,
      catalogItems: {
        products: []
      },
      previewVisible: false,
      previewImage: ""
    };
    this.id = props.mapping ? props.mapping.id : ulid();
  }

  async componentDidMount() {
    const { mapping } = this.props;
    if (mapping) {
      const images = await Promise.all(
        this.props.mapping.images.map(i => Storage.get(`resized/${i}`))
      );

      this.props.form.setFieldsValue({
        mappingId: mapping.id,
        name: mapping.name,
        sku: mapping.sku,
        catalogId: mapping.catalogId,
        product_pos_code: mapping.product_pos_code,
        quantity: mapping.quantity,
        customized: mapping.customized,
        images: images.map((url, idx) => {
          const key = this.props.mapping.images[idx];
          const parts = key.split("/");
          const name = last(parts);
          return {
            url,
            name,
            uid: key,
            status: "done",
            response: {
              key
            }
          };
        })
      });
      this.setState({ mapping });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const touched = this.props.form.isFieldsTouched([
      "name",
      "sku",
      "catalogId",
      "product_pos_code",
      "quantity",
      "customized",
      "images"
    ]);
    this.props.onChange && this.props.onChange(touched);
  }

  getInitialName = () => {
    const { listing, product } = this.props;
    if (!listing || !product) return "";
    let name = listing.title;
    const options = product.property_values.map(i => i.values[0]).join("-");
    if (options) {
      name = name + "-" + options;
    }
    return name;
  };

  normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  onCatalogChange = async val => {
    this.props.form.setFieldsValue({
      product_pos_code: undefined
    });
  };

  onSubmit = (e, closeScreen) => {
    e && e.preventDefault();
    const { catalogs, product, listing } = this.props;
    this.props.form.validateFields((err, values) => {
      if (!err) {
        if (listing && product) {
          values.productId = `etsy:${listing.listing_id}:${product.product_id}`;
          values.title = listing.title;
          values.productOptions = product.property_values.map(p => ({
            name: p.property_name,
            values: p.values
          }));
        }
        if (values.mappingId === 'ignore') {
          values.id = this.id;
          values.ignore = true;
        }
        else if (!values.mappingId || values.mappingId === "new") {
          delete values.mappingId;
          values.id = this.id;
          const catalog = this.props.catalogs.find(
            cat => cat.cat_id === values.catalogId
          );
          const products = catalogs.find(c => c.cat_id === values.catalogId).products;
          const sosProduct = products.find(
            p => p.pos_code === values.product_pos_code
          );
          values.catalogName = catalog.cat_name;
          values.product_size = sosProduct.size;
          values.images = values.images.map(i => i.response.key);
        }
        this.setState({ saving: true });
        this.props.onSave(values).then(
          resp => {
            message.success("Mapping saved");
            this.setState({ saving: false, mapping: resp });
            if (closeScreen !== false) {
              this.props.onClose && this.props.onClose();
            }
          },
          err => {
            message.error(err);
            this.setState({ saving: false });
          }
        );
      }
    });
  };

  handleUpload = ({
    action,
    data,
    file,
    filename,
    headers,
    onError,
    onProgress,
    onSuccess
  }) => {
    const bucket = makeBucketName(this.id, file.name);
    Storage.vault
      .put(bucket, file, {
        progressCallback: progress => {
          const pc = (progress.loaded / progress.total) * 100;
          onProgress({ percent: pc }, file);
        }
      })
      .then(result => {
        onSuccess(result, file);
      })
      .catch(err => {
        onError(err);
      });
  };

  onRemove = e => {
    if (e.response) {
      const key = e.response.key;
      Storage.vault.remove(key).then(
        () => {
          deletePublicImage(key);
          message.success("File removed");
          if (this.state.mapping) {
            this.onSubmit(null, false);
          }
        },
        e => message.error(e.message)
      );
    }
  };

  handlePreview = file => {
    this.setState({
      previewImage: file.response.key,
      previewVisible: true
    });
  };

  handleCancel = () => this.setState({ previewVisible: false });

  onSearch = query => {
    if (query.length >= 2)
      this.props.dispatch(mappingActions.searchMappings(query));
  };

  render() {
    const {
      form: { getFieldDecorator, getFieldValue },
      catalogs,
      labData,
      product,
      mappings,
      mapping,
      searching,
      onClose
    } = this.props;
    const { catalogItems, saving, previewImage, previewVisible } = this.state;

    if (!labData) return null;

    const fileTypes =
      catalogItems.allowed_file_types || labData.allowed_file_types;
    const extensions =
      fileTypes &&
      fileTypes
        .split(",")
        .map(i => `.${i}`)
        .join(",");

    const catalogId = getFieldValue("catalogId");
    const mappingId = getFieldValue("mappingId");
    const files = getFieldValue("images");

    const hasPendingUploads =
      mappingId && mappingId !== "new"
        ? false
        : files
        ? !!files.find(f => !f.response)
        : true;

    /*
      show mapping form if we're editing mapping or creating new mapping
     */
    const showMappingForm =
      mappingId === "new" || (!mappingId && mapping) || !product;

    let products = [];

    if (catalogId) {
      products = catalogs.find(c => c.cat_id === catalogId).products;
    }

    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 }
      }
    };

    const uploadButton = (
      <div>
        <Button type="primary" icon="upload" title="RGB JPEG files only" ghost>
          Click to select
        </Button>
      </div>
    );

    return (
      <Styled.Container>
        <Form onSubmit={this.onSubmit} hideRequiredMark>
          {product && !this.props.mapping ? (
            <FormItem>
              {getFieldDecorator("mappingId", {
                initialValue: !product ? "new" : undefined,
                rules: [{ required: true, message: "Please select mapping" }]
              })(
                <Select
                  showSearch
                  size="large"
                  placeholder="Search for an existing mapping or create a new one"
                  onSearch={this.onSearch}
                  filterOption={false}
                  loading={searching}
                >
                  <Select.Option value="new">
                    <strong>Create a new mapping</strong>
                  </Select.Option>
                  <Select.Option value="ignore">
                    <strong>Ignore this product</strong>
                  </Select.Option>
                  {mappings.map(m => (
                    <Select.Option value={m.id}>
                      {m.name} - SKU:{m.sku}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </FormItem>
          ) : null}
          {mappingId === "ignore" && (
            <p>
              <Icon type="info-circle" /> Orders with this product will be
              ignored & hidden from orders list
            </p>
          )}
          {showMappingForm && (
            <Fragment>
              <Row gutter={12}>
                <Col lg={18} sm={12}>
                  <FormItem label="Mapping name">
                    {getFieldDecorator("name", {
                      initialValue: this.getInitialName(),
                      rules: [{ required: true }]
                    })(
                      <Input placeholder="e.g., Cutest Prints Ever, Set of 4 8x10s" />
                    )}
                  </FormItem>
                </Col>
                <Col lg={6} sm={12}>
                  <FormItem label="SKU">
                    {getFieldDecorator("sku", { rules: [{ required: false }] })(
                      <Input placeholder="Optional" />
                    )}
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col lg={8} sm={8}>
                  <FormItem>
                    {getFieldDecorator("catalogId", {
                      rules: [{ required: true }]
                    })(
                      <Select
                        onChange={this.onCatalogChange}
                        placeholder="Please select catalog"
                      >
                        {catalogs.map(c => (
                          <Select.Option value={c.cat_id} key={c.cat_id}>
                            {c.cat_name}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </FormItem>
                </Col>
                <Col lg={8} sm={8}>
                  <FormItem>
                    {getFieldDecorator("product_pos_code", {
                      rules: [{ required: true }]
                    })(
                      <Select placeholder="Please select product">
                        {products.map(p => (
                          <Select.Option value={p.pos_code} key={p.pos_code}>
                            {p.size} (${getTirePrice(p.price)} ea)
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </FormItem>
                </Col>
                <Col lg={8} sm={8}>
                  <FormItem label="Quantity" {...formItemLayout}>
                    {getFieldDecorator("quantity", {
                      rules: [{ required: true }],
                      initialValue: 1
                    })(
                      <InputNumber
                        min={1}
                        max={1000}
                        style={{ width: "100%" }}
                      />
                    )}
                  </FormItem>
                </Col>
              </Row>
              <FormItem>
                {getFieldDecorator("customized", {
                  valuePropName: "checked"
                })(
                  <Checkbox>
                    Multiple choice item (Etsy only){" "}
                    <Tooltip title="Check this box for listings that allow the buyer to choose a subset of items from a larger set.">
                      <Icon type="info-circle" />
                    </Tooltip>
                  </Checkbox>
                )}
              </FormItem>
              <FormItem label="Upload Images - Must be RGB, JPG's only">
                {getFieldDecorator("images", {
                  valuePropName: "fileList",
                  getValueFromEvent: this.normFile,
                  rules: [{ required: true }]
                })(
                  <Upload
                    multiple
                    showUploadList
                    accept={extensions}
                    listType="picture"
                    onPreview={this.handlePreview}
                    customRequest={this.handleUpload}
                    beforeUpload={validImage}
                    onRemove={this.onRemove}
                  >
                    {uploadButton}
                  </Upload>
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator("tos", {
                  valuePropName: "checked",
                  initialValue: !!mapping,
                  rules: [{ validator: tosValidator }]
                })(
                  <Checkbox className="multiline">
                    I represent and warrant that I possess all necessary rights
                    with respect to these images, and that they do not infringe
                    or otherwise violate any copyright, trade secret right or
                    other intellectual property or other property or proprietary
                    right of any third party. I  agree to indemnify and hold Fromex Photo Lab, Inc and Qprints.com
                     and their directors, officers and employees harmless from and against any and all claims, liabilities, causes of action,
                     damages, losses and expenses (including attorneys’ fees) that may arise from printing these images.
                  </Checkbox>
                )}
              </FormItem>
            </Fragment>
          )}
          <div className="text-right">
            {onClose && (
              <Button size="large" onClick={onClose}>
                Close
              </Button>
            )}{" "}
            <Button
              disabled={hasPendingUploads}
              loading={saving}
              type="primary"
              size="large"
              icon="save"
              htmlType="submit"
            >
              Save
            </Button>
          </div>
        </Form>
        {previewVisible && (
          <Preview fileKey={previewImage} onClose={this.handleCancel} />
        )}
      </Styled.Container>
    );
  }
}

export default connect(state => ({
  ...state.lab,
  mappings: state.mappings.searchResults,
  searching: state.mappings.searching
}))(Form.create()(Mapping));
